import { useEffect, useMemo, useState } from "react";
import {
  useCreateHelpArticle,
  useCreateHelpCollection,
  useDeleteHelpArticle,
  useDeleteHelpCollection,
  useHelpArticle,
  useHelpCollection,
  useHelpCollections,
  useUpdateHelpArticle,
  useUpdateHelpCollection,
} from "@/hooks/useHelpCenter";
import { HelpArticle, HelpArticleInput, HelpAuthorInput, HelpCollectionInput } from "@/types/help";
import { Author } from "@/types/authors";
import { useAuthors } from "@/hooks/useAuthors";
import AuthorSelector from "@/components/AuthorSelector";
import ImageUploader from "@/components/ImageUploader";
import MarkdownEditor from "@/components/MarkdownEditor";
import { Button, PageHeader, Badge, Toggle } from "@/components/ui";
import { Icon } from "@/components/IconSprite";
import { clsx } from "clsx";

interface CollectionFormState {
  title: string;
  summary: string;
  description: string;
  heroImageUrl: string;
  orderIndex: number;
  isPublished: boolean;
  slug: string;
}

interface ArticleFormState {
  title: string;
  summary: string;
  content: string;
  heroImageUrl: string;
  estimatedReadMinutes: number | null;
  orderIndex: number;
  isPublished: boolean;
  publishedAt: string | null;
  slug: string;
  authors: HelpAuthorInput[];
  relatedArticleIds: string[];
}

const defaultCollectionForm = (): CollectionFormState => ({
  title: "",
  summary: "",
  description: "",
  heroImageUrl: "",
  orderIndex: 0,
  isPublished: false,
  slug: "",
});

const defaultArticleForm = (): ArticleFormState => ({
  title: "",
  summary: "",
  content: "",
  heroImageUrl: "",
  estimatedReadMinutes: null,
  orderIndex: 0,
  isPublished: false,
  publishedAt: null,
  slug: "",
  authors: [],
  relatedArticleIds: [],
});

const toDatetimeLocal = (iso: string | null): string => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60_000);
  return local.toISOString().slice(0, 16);
};

const mapArticleToForm = (article: HelpArticle): ArticleFormState => ({
  title: article.title,
  summary: article.summary ?? "",
  content: article.content ?? "",
  heroImageUrl: article.heroImageUrl ?? "",
  estimatedReadMinutes: article.estimatedReadMinutes ?? null,
  orderIndex: article.orderIndex ?? 0,
  isPublished: article.isPublished,
  publishedAt: article.publishedAt ?? null,
  slug: article.slug ?? "",
  authors: article.authors.map((a) => ({
    authorId: a.authorId,
    displayName: a.displayName,
    title: a.title ?? "",
    avatarUrl: a.avatarUrl ?? "",
  })),
  relatedArticleIds: article.relatedArticleIds ?? [],
});

const sanitizeCollectionPayload = (form: CollectionFormState): HelpCollectionInput => ({
  title: form.title.trim(),
  summary: form.summary.trim() || null,
  description: form.description.trim() || null,
  heroImageUrl: form.heroImageUrl.trim() || null,
  orderIndex: Number.isFinite(form.orderIndex) ? form.orderIndex : 0,
  isPublished: form.isPublished,
  slug: form.slug.trim() || null,
});

const sanitizeArticlePayload = (form: ArticleFormState): HelpArticleInput => ({
  title: form.title.trim(),
  summary: form.summary.trim() || null,
  content: form.content,
  heroImageUrl: form.heroImageUrl.trim() || null,
  estimatedReadMinutes: form.estimatedReadMinutes ?? null,
  orderIndex: Number.isFinite(form.orderIndex) ? form.orderIndex : 0,
  isPublished: form.isPublished,
  publishedAt: form.publishedAt,
  slug: form.slug.trim() || null,
  authors: form.authors
    .map((a) => ({
      authorId: a.authorId ?? undefined,
      displayName: a.displayName.trim(),
      title: a.title?.trim() || null,
      avatarUrl: a.avatarUrl?.trim() || null,
    }))
    .filter((a) => a.displayName.length > 0),
  relatedArticleIds: form.relatedArticleIds,
});

const HelpCenter = () => {
  const { data: collections, isLoading: collectionsLoading } = useHelpCollections();
  const { data: authors } = useAuthors();
  const [selectedCollectionId, setSelectedCollectionId] = useState<string | null>(null);
  const [creatingCollection, setCreatingCollection] = useState(false);
  const [collectionForm, setCollectionForm] = useState<CollectionFormState>(defaultCollectionForm);

  const { data: collectionDetail } = useHelpCollection(selectedCollectionId);

  const [selectedArticleId, setSelectedArticleId] = useState<string | null>(null);
  const [creatingArticle, setCreatingArticle] = useState(false);
  const { data: articleDetail } = useHelpArticle(!creatingArticle ? selectedArticleId : null);
  const [articleForm, setArticleForm] = useState<ArticleFormState>(defaultArticleForm);

  const createCollection = useCreateHelpCollection();
  const updateCollection = useUpdateHelpCollection();
  const deleteCollection = useDeleteHelpCollection();
  const createArticle = useCreateHelpArticle();
  const updateArticle = useUpdateHelpArticle();
  const deleteArticle = useDeleteHelpArticle();

  useEffect(() => {
    if (!collectionsLoading && collections?.length && !selectedCollectionId && !creatingCollection) {
      setSelectedCollectionId(collections[0].collectionId);
    }
  }, [collections, collectionsLoading, selectedCollectionId, creatingCollection]);

  useEffect(() => {
    if (creatingCollection) {
      setCollectionForm(defaultCollectionForm());
      return;
    }
    if (!collectionDetail) {
      setCollectionForm(defaultCollectionForm());
      return;
    }
    setCollectionForm({
      title: collectionDetail.title,
      summary: collectionDetail.summary ?? "",
      description: collectionDetail.description ?? "",
      heroImageUrl: collectionDetail.heroImageUrl ?? "",
      orderIndex: collectionDetail.orderIndex ?? 0,
      isPublished: collectionDetail.isPublished,
      slug: collectionDetail.slug ?? "",
    });
  }, [collectionDetail, creatingCollection]);

  useEffect(() => {
    if (!collectionDetail || creatingArticle) return;
    if (!collectionDetail.articles.length) {
      setSelectedArticleId(null);
      setArticleForm(defaultArticleForm());
      return;
    }
    if (!selectedArticleId || !collectionDetail.articles.some((a) => a.articleId === selectedArticleId)) {
      setSelectedArticleId(collectionDetail.articles[0].articleId);
    }
  }, [collectionDetail, creatingArticle, selectedArticleId]);

  useEffect(() => {
    if (creatingArticle) {
      setArticleForm(defaultArticleForm());
      return;
    }
    if (articleDetail) {
      setArticleForm(mapArticleToForm(articleDetail));
    } else if (!selectedArticleId) {
      setArticleForm(defaultArticleForm());
    }
  }, [articleDetail, creatingArticle, selectedArticleId]);

  const articleOptions = useMemo(() => collectionDetail?.articles ?? [], [collectionDetail]);

  const handleSelectCollection = (collectionId: string) => {
    setCreatingCollection(false);
    setSelectedCollectionId(collectionId);
    setSelectedArticleId(null);
    setCreatingArticle(false);
  };

  const handleStartNewCollection = () => {
    setCreatingCollection(true);
    setSelectedCollectionId(null);
    setCollectionForm(defaultCollectionForm());
    setCreatingArticle(false);
    setSelectedArticleId(null);
  };

  const handleSaveCollection = () => {
    const payload = sanitizeCollectionPayload(collectionForm);
    if (!payload.title) return;
    if (creatingCollection) {
      createCollection.mutate(payload, {
        onSuccess: (c) => {
          setCreatingCollection(false);
          setSelectedCollectionId(c.collectionId);
        },
      });
    } else if (selectedCollectionId) {
      updateCollection.mutate({ collectionId: selectedCollectionId, payload });
    }
  };

  const handleDeleteCollection = () => {
    if (!selectedCollectionId) return;
    deleteCollection.mutate(selectedCollectionId, {
      onSuccess: () => {
        setSelectedCollectionId(null);
        setCreatingCollection(false);
        setCollectionForm(defaultCollectionForm());
        setCreatingArticle(false);
        setSelectedArticleId(null);
      },
    });
  };

  const handleStartNewArticle = () => {
    setCreatingArticle(true);
    setSelectedArticleId(null);
    setArticleForm(defaultArticleForm());
  };

  const handleSelectArticle = (articleId: string) => {
    setCreatingArticle(false);
    setSelectedArticleId(articleId);
  };

  const handleSaveArticle = () => {
    if (!selectedCollectionId) return;
    const payload = sanitizeArticlePayload(articleForm);
    if (!payload.title || !payload.content) return;
    if (creatingArticle) {
      createArticle.mutate(
        { collectionId: selectedCollectionId, payload },
        {
          onSuccess: ({ article }) => {
            setCreatingArticle(false);
            setSelectedArticleId(article.articleId);
          },
        }
      );
    } else if (selectedArticleId) {
      updateArticle.mutate({ articleId: selectedArticleId, payload });
    }
  };

  const handleDeleteArticle = () => {
    if (!selectedArticleId || !selectedCollectionId) return;
    deleteArticle.mutate(
      { articleId: selectedArticleId },
      {
        onSuccess: () => {
          setSelectedArticleId(null);
          setCreatingArticle(false);
          setArticleForm(defaultArticleForm());
        },
      }
    );
  };

  const toggleRelatedArticle = (articleId: string) => {
    setArticleForm((prev) => ({
      ...prev,
      relatedArticleIds: prev.relatedArticleIds.includes(articleId)
        ? prev.relatedArticleIds.filter((id) => id !== articleId)
        : [...prev.relatedArticleIds, articleId],
    }));
  };

  const updateAuthorField = (index: number, field: keyof HelpAuthorInput, value: string) => {
    setArticleForm((prev) => {
      const next = [...prev.authors];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, authors: next };
    });
  };

  const addAuthor = () => {
    setArticleForm((prev) => ({
      ...prev,
      authors: [...prev.authors, { displayName: "", title: "", avatarUrl: "" }],
    }));
  };

  const addAuthorFromSelector = (author: Author | null) => {
    if (!author) return;
    // Check if author is already added
    const exists = articleForm.authors.some(
      (a) => a.authorId === author.authorId || a.displayName === author.displayName
    );
    if (exists) return;
    setArticleForm((prev) => ({
      ...prev,
      authors: [
        ...prev.authors,
        {
          authorId: author.authorId,
          displayName: author.displayName,
          title: author.title || "",
          avatarUrl: author.avatarUrl || "",
        },
      ],
    }));
  };

  const removeAuthor = (index: number) => {
    setArticleForm((prev) => ({
      ...prev,
      authors: prev.authors.filter((_, i) => i !== index),
    }));
  };

  const collectionBusy = createCollection.isPending || updateCollection.isPending || deleteCollection.isPending;
  const articleBusy =
    createArticle.isPending || updateArticle.isPending || deleteArticle.isPending || (!creatingArticle && !articleDetail && !!selectedArticleId);

  return (
    <div className="page wide">
      <PageHeader
        label="Content"
        title="Help Center"
        description="Manage collections and articles surfaced in the assistant's Help tab."
      />

      <div className="md a">
        {/* Collections Sidebar */}
        <div className="card" style={{ position: "sticky", top: 0 }}>
          <div className="card-h tight">
            <div>
              <h2>Collections</h2>
            </div>
            <div className="r">
              <Button variant="outline" size="sm" onClick={handleStartNewCollection}>
                + New
              </Button>
            </div>
          </div>
          <div className="list-scroll">
            {collectionsLoading && <p className="meta" style={{ padding: "12px 14px" }}>Loading...</p>}
            {collections?.map((c) => (
              <button
                key={c.collectionId}
                type="button"
                onClick={() => handleSelectCollection(c.collectionId)}
                className={clsx("list-i", selectedCollectionId === c.collectionId && !creatingCollection && "on")}
              >
                <span
                  className={clsx("av", selectedCollectionId === c.collectionId && !creatingCollection && "g")}
                  style={{ width: 24, height: 24, borderRadius: 6 }}
                >
                  <Icon name="book" />
                </span>
                <span className="tx">
                  <b>{c.title}</b>
                  <span>{c.articleCount} articles</span>
                </span>
              </button>
            ))}
            {collections && !collections.length && !collectionsLoading && (
              <p className="meta" style={{ padding: "12px 14px" }}>No collections yet.</p>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="stack">
          {/* Collection Form */}
          <div className="card">
            <div className="card-h">
              <div>
                <h2>
                  {creatingCollection ? "New Collection" : collectionDetail?.title ?? "Select a collection"}
                  {!creatingCollection && collectionDetail && (
                    <Badge variant={collectionDetail.isPublished ? "success" : "default"}>
                      {collectionDetail.isPublished ? "Live" : "Draft"}
                    </Badge>
                  )}
                </h2>
              </div>
              <div className="r">
                {!creatingCollection && selectedCollectionId && (
                  <Button variant="danger" size="sm" onClick={handleDeleteCollection}>
                    <Icon name="trash" />
                    Delete
                  </Button>
                )}
                <Button size="sm" onClick={handleSaveCollection} loading={collectionBusy}>
                  Save
                </Button>
              </div>
            </div>
            <div className="card-b">
              <div className="row c2">
                <div className="fg">
                  <label className="fl">Title <span className="req">*</span></label>
                  <input
                    className="inp"
                    value={collectionForm.title}
                    onChange={(e) => setCollectionForm((p) => ({ ...p, title: e.target.value }))}
                    placeholder="Collection title"
                  />
                </div>
                <div className="fg">
                  <label className="fl">Slug</label>
                  <input
                    className="inp mono"
                    value={collectionForm.slug}
                    onChange={(e) => setCollectionForm((p) => ({ ...p, slug: e.target.value }))}
                    placeholder="collection-slug"
                  />
                </div>
              </div>
              <div className="fg">
                <label className="fl">Summary</label>
                <textarea
                  className="ta"
                  value={collectionForm.summary}
                  onChange={(e) => setCollectionForm((p) => ({ ...p, summary: e.target.value }))}
                  rows={2}
                />
              </div>
              <div className="fg">
                <label className="fl">Description</label>
                <textarea
                  className="ta"
                  value={collectionForm.description}
                  onChange={(e) => setCollectionForm((p) => ({ ...p, description: e.target.value }))}
                  rows={3}
                />
              </div>
              <div className="fg">
                <label className="fl">Hero Image</label>
                <ImageUploader
                  value={collectionForm.heroImageUrl || null}
                  onChange={(url) => setCollectionForm((p) => ({ ...p, heroImageUrl: url || "" }))}
                  placeholder="Drop image or browse library"
                />
              </div>
              <div className="row c2">
                <div className="fg">
                  <label className="fl">Order</label>
                  <input
                    className="inp"
                    type="number"
                    value={collectionForm.orderIndex}
                    onChange={(e) => setCollectionForm((p) => ({ ...p, orderIndex: Number(e.target.value) || 0 }))}
                  />
                </div>
                <div className="fg">
                  <label className="fl">Published</label>
                  <div style={{ display: "flex", alignItems: "center", gap: 9, height: 34 }}>
                    <Toggle
                      checked={collectionForm.isPublished}
                      onChange={(checked) => setCollectionForm((p) => ({ ...p, isPublished: checked }))}
                      size="sm"
                    />
                    <span style={{ fontSize: "12.5px", color: "var(--ink-3)", fontWeight: 560 }}>
                      Visible in Help tab
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Articles Section */}
          <div className="card">
            <div className="card-h tight">
              <div>
                <h2>
                  Articles
                  {collectionDetail && <Badge variant="default">{collectionDetail.articles.length} articles</Badge>}
                </h2>
              </div>
              <div className="r">
                {collectionDetail && (
                  <Button variant="outline" size="sm" onClick={handleStartNewArticle}>
                    + New Article
                  </Button>
                )}
              </div>
            </div>

            {collectionDetail ? (
              <>
                <div className="card-b flush">
                  <div className="md" style={{ gridTemplateColumns: "200px 1fr", gap: 0 }}>
                    {/* Article List */}
                    <div style={{ borderRight: "1px solid var(--line)" }}>
                      <div className="list-scroll" style={{ maxHeight: 640 }}>
                        {collectionDetail.articles.map((a) => (
                          <button
                            key={a.articleId}
                            type="button"
                            onClick={() => handleSelectArticle(a.articleId)}
                            className={clsx("list-i", !creatingArticle && selectedArticleId === a.articleId && "on")}
                          >
                            <span className="tx">
                              <b>{a.title}</b>
                              <span style={{ display: "flex", gap: 5, marginTop: 4 }}>
                                <Badge variant={a.isPublished ? "success" : "default"}>
                                  {a.isPublished ? "Live" : "Draft"}
                                </Badge>
                              </span>
                            </span>
                          </button>
                        ))}
                        {!collectionDetail.articles.length && (
                          <p className="meta" style={{ padding: "12px 14px" }}>No articles yet.</p>
                        )}
                      </div>
                    </div>

                    {/* Article Form */}
                    <div style={{ padding: 16 }}>
                      <div className="lbl-row" style={{ marginBottom: 12 }}>
                        <b style={{ fontSize: 13, fontWeight: 650, letterSpacing: "-.01em" }}>
                          {creatingArticle ? "New Article" : articleDetail?.title ?? "Select an article"}
                        </b>
                      </div>

                      <div className="row c2">
                        <div className="fg">
                          <label className="fl">Title <span className="req">*</span></label>
                          <input
                            className="inp"
                            value={articleForm.title}
                            onChange={(e) => setArticleForm((p) => ({ ...p, title: e.target.value }))}
                          />
                        </div>
                        <div className="fg">
                          <label className="fl">Slug</label>
                          <input
                            className="inp mono"
                            value={articleForm.slug}
                            onChange={(e) => setArticleForm((p) => ({ ...p, slug: e.target.value }))}
                          />
                        </div>
                      </div>

                      <div className="fg">
                        <label className="fl">Summary</label>
                        <textarea
                          className="ta"
                          value={articleForm.summary}
                          onChange={(e) => setArticleForm((p) => ({ ...p, summary: e.target.value }))}
                          rows={2}
                        />
                      </div>

                      <div className="fg">
                        <div className="lbl-row">
                          <label className="fl">Content <span className="req">*</span></label>
                          <span className="cnt">Markdown</span>
                        </div>
                        <MarkdownEditor
                          value={articleForm.content}
                          onChange={(value) => setArticleForm((p) => ({ ...p, content: value }))}
                          placeholder="Write your article content here..."
                          height={350}
                        />
                      </div>

                      <div className="fg">
                        <label className="fl">Hero Image</label>
                        <ImageUploader
                          value={articleForm.heroImageUrl || null}
                          onChange={(url) => setArticleForm((p) => ({ ...p, heroImageUrl: url || "" }))}
                          placeholder="Drop image or browse library"
                        />
                      </div>

                      <div className="row c3">
                        <div className="fg">
                          <label className="fl">Read Time <span className="opt">min</span></label>
                          <input
                            className="inp"
                            type="number"
                            value={articleForm.estimatedReadMinutes ?? ""}
                            onChange={(e) => setArticleForm((p) => ({ ...p, estimatedReadMinutes: e.target.value ? Number(e.target.value) : null }))}
                          />
                        </div>
                        <div className="fg">
                          <label className="fl">Order</label>
                          <input
                            className="inp"
                            type="number"
                            value={articleForm.orderIndex}
                            onChange={(e) => setArticleForm((p) => ({ ...p, orderIndex: Number(e.target.value) || 0 }))}
                          />
                        </div>
                        <div className="fg">
                          <label className="fl">Published At</label>
                          <input
                            className="inp"
                            type="datetime-local"
                            value={toDatetimeLocal(articleForm.publishedAt)}
                            onChange={(e) => setArticleForm((p) => ({ ...p, publishedAt: e.target.value ? new Date(e.target.value).toISOString() : null }))}
                          />
                        </div>
                      </div>

                      <div className="fg">
                        <label className="fl">Published</label>
                        <div style={{ display: "flex", alignItems: "center", gap: 9, height: 34 }}>
                          <Toggle
                            checked={articleForm.isPublished}
                            onChange={(checked) => setArticleForm((p) => ({ ...p, isPublished: checked }))}
                            size="sm"
                          />
                          <span style={{ fontSize: "12.5px", color: "var(--ink-3)", fontWeight: 560 }}>Live</span>
                        </div>
                      </div>

                      {/* Authors */}
                      <div className="sec">
                        <div className="sec-h">
                          <b>Authors</b>
                          <span>Bylines shown at the top of the article.</span>
                        </div>

                        {/* Author Selector */}
                        <div className="fg" style={{ marginBottom: 10 }}>
                          <AuthorSelector
                            value={null}
                            onChange={addAuthorFromSelector}
                            placeholder="Add an author..."
                          />
                        </div>

                        {/* Selected Authors */}
                        {articleForm.authors.length === 0 && (
                          <p className="meta">No authors set. Use the selector above to add authors.</p>
                        )}
                        <div className="chips">
                          {articleForm.authors.map((author, i) => (
                            <span key={i} className="chip on" style={{ height: 32, padding: "0 5px", gap: 7 }}>
                              {author.avatarUrl ? (
                                <img
                                  src={author.avatarUrl}
                                  alt=""
                                  style={{ width: 22, height: 22, borderRadius: 6, objectFit: "cover" }}
                                />
                              ) : (
                                <span className="av" style={{ width: 22, height: 22, borderRadius: 6, fontSize: 9 }}>
                                  {author.displayName.charAt(0).toUpperCase()}
                                </span>
                              )}
                              <span style={{ textAlign: "left", lineHeight: 1.2 }}>
                                <b style={{ display: "block", fontSize: "11.5px", fontWeight: 640 }}>
                                  {author.displayName}
                                </b>
                                {author.title && (
                                  <span style={{ display: "block", fontSize: "9.5px", color: "var(--ink-4)", fontWeight: 520 }}>
                                    {author.title}
                                  </span>
                                )}
                              </span>
                              <button type="button" className="x" onClick={() => removeAuthor(i)}>
                                <Icon name="x" />
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Related Articles */}
                      <div className="sec">
                        <div className="sec-h">
                          <b>Related Articles</b>
                          <span>Suggested next reads in the Help tab.</span>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                          {articleOptions
                            .filter((a) => a.articleId !== selectedArticleId)
                            .map((a) => {
                              const checked = articleForm.relatedArticleIds.includes(a.articleId);
                              return (
                                <label key={a.articleId} className="cb-row">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleRelatedArticle(a.articleId)}
                                    style={{ display: "none" }}
                                  />
                                  <span className={clsx("cb", checked && "on")}>
                                    <Icon name="check" />
                                  </span>
                                  {a.title}
                                </label>
                              );
                            })}
                          {articleOptions.filter((a) => a.articleId !== selectedArticleId).length === 0 && (
                            <p className="meta">No other articles.</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card-f">
                  <div className="r">
                    {!creatingArticle && selectedArticleId && (
                      <Button variant="danger" size="sm" onClick={handleDeleteArticle}>
                        <Icon name="trash" />
                        Delete
                      </Button>
                    )}
                    <Button size="sm" onClick={handleSaveArticle} loading={articleBusy}>
                      Save Article
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="card-b">
                <p className="meta">Select a collection</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpCenter;
